import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { HashMap } from 'shared/types/commonView';

export type ChartValuesAndLabels = [number[], string[]];
export interface ChartNumberResult {
  value: number | undefined;
  maxValue?: number;
}
export type TripletSeriesWithLabels = [
  TripletSeries,
  [string, string, string],
];
export type TripletSeries = [
  ChartNumberResult,
  ChartNumberResult,
  ChartNumberResult,
];
export enum IssueChart {
  originator = 'originator',
  rootCause = 'rootCause',
  impact = 'impact',
  workType = 'workType',
  environmentalAspect = 'environmentalAspect',
  hsEffect = 'hsEffect',
  hsHazardType = 'hsHazardType',
}

export enum InspectionChart {
  inspectionTriplet = 'inspectionTriplet',
  completedInspectionsByTemplate = 'completedInspectionsByTemplate',
  inspectionEvolution = 'inspectionEvolution',
}

export function isIssueChart(chart: IssueChart | InspectionChart): chart is IssueChart {
  return Object.values<string>(IssueChart).includes(chart);
}

export interface FilterValues {
  dataScope: FilterValueGroup;
  chartSettings?: FilterValueGroup;
  presented?: FilterValueGroup;
}

interface FilterValueGroup {
  labelId: string;
  filters: FilterValue[];
}

export interface FilterValue {
  labelId: string;
  value: any;
}

export interface EstimatedCost {
  cost: number;
}

export interface FinalCost {
  outstanding: number;
  settled: number;
}

export interface Finances {
  estimatedCosts: HashMap<EstimatedCost>;
  finalCosts: HashMap<FinalCost>;
  expectedFines: HashMap<number>;
  imposedFines: HashMap<number>;
}

// TODO: now
export type EstimatedSingleCost = any;
export type FinalSingleCost = any;
export type CostsUnion = EstimatedSingleCost;

export type CalculationFunction<T> = (current: T, cost: any) => T;

export interface DateRangeObject {
  from: string;
  to: string;
}

export function isDateRangeObject(value: unknown): value is DateRangeObject {
  return (
    typeof value === 'object' &&
    value !== null &&
    'from' in value &&
    'to' in value &&
    typeof value.from === 'string' &&
    typeof value.to === 'string'
  );
}
export interface AnyFilters<T = any, P = undefined, K = undefined> {
  dataScope: ChartFilterGroup<T>;
  chartSettings?: ChartFilterGroup<P>;
  presented?: ChartFilterGroup<K>;
}

export interface ChartFilterGroup<T> {
  labelId: string;
  filters: ChartFilter<T>[];
}

export interface ChartFilter<T> {
  labelId: string;
  type: FilterTypes;
  available?: any[];
  labels?: HashMap<string>;
  value: T;
  setter: (...args: any) => void;
  toUrlQuery?: (value: any) => string;
  fieldRelation?: IssueFieldNames;
}
// taken directly from declaration file

export type ChartAxisSeries = ChartAxisSeriesItem[];
export interface ChartAxisSeriesItem {
  name?: string;
  type?: string;
  color?: string;
  labels?: string[];
  data:
    | (number | null)[]
    | {
        x: any;
        y: any;
        fillColor?: string;
        strokeColor?: string;
        meta?: any;
        goals?: any;
      }[]
    | [number, number | null][]
    | [number, (number | null)[]][];
}

// These strict types are used to ensure the value has no additional properties
// so we can safely pass values between react and service worker
export type StrictDateRange = DateRangeObject & { __value__?: never };
export type StrictDateInterval = DateRangeIntervalWithoutWeek & {
  __value__?: never;
};
export interface CompletedInspectionsByTemplateFilters {
  dataScope: {
    labelId: string;
    filters: [
      ChartFilter<StrictDateRange>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
    ];
  };
}
export interface InspectionEvolutionFilters {
  dataScope: {
    labelId: string;
    filters: [
      ChartFilter<DateRangeIntervalWithoutWeek & { __value__?: never }>,
      ChartFilter<StrictDateRange>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
    ];
  };
}
export interface InspectionTripletFilters {
  dataScope: {
    labelId: string;
    filters: [
      ChartFilter<StrictDateRange>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
      ChartFilter<string[]>,
    ];
  };
}

export type IssueChartFilterValue = string[] | DateRangeObject;

/** Filters of an issue chart: the date range, then one multiselect per filter kind. */
export interface IssueChartFilters {
  dataScope: ChartFilterGroup<IssueChartFilterValue>;
}

export type ChartFiltersUnion =
  | IssueChartFilters
  | DetectedResolvedFilters;
export enum FilterTypes {
  radio = 'radio',
  checkbox = 'checkbox',
  segments = 'segments',
  multiselect = 'multiselect',
  daterange = 'daterange',
  select = 'select',
}
export interface DetectedResolvedFilters {
  chartSettings: ChartFilterGroup<ChartSettingsFilters>;
  dataScope: ChartFilterGroup<DataScopeFilters>;
  presented: ChartFilterGroup<PresentedFilters>;
}
export type ChartSettingsFilters = string | ChartType | HashMap<boolean>;
export type DataScopeFilters =
  | string
  | DateRangeInterval
  | string[]
  | DateRangeObject
  | HashMap<boolean>;
export type PresentedFilters = Record<PresentedTypes, boolean>;
export enum DateRangeInterval {
  day = 'day',
  week = 'week',
  month = 'month',
  year = 'year',
}
export enum DateRangeIntervalWithoutWeek {
  day = 'day',
  month = 'month',
  year = 'year',
}
export enum PresentedTypes {
  detected = 'detected',
  resolved = 'resolved',
}
export enum ChartType {
  bar = 'bar',
  area = 'area',
  line = 'line',
  donut = 'donut',
  radialBar = 'radialBar',
  number = 'number',
  triplet = 'triplet',
}
